import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useSettings } from '../app/context';

/** System preference combined with the in-app "Reduce motion" setting. */
export function useReduceMotion(): boolean {
  const system = useReducedMotion() ?? false;
  const pref = useSettings((s) => s.reducedMotion);
  return pref === 'on' || (pref === 'system' && system);
}

/** Seconds remaining until a server-time deadline, ticking 4×/s. */
export function useCountdown(deadline: number | null, serverNow: () => number): number | null {
  const compute = () =>
    deadline === null ? null : Math.max(0, Math.ceil((deadline - serverNow()) / 1000));
  const [left, setLeft] = useState(compute);
  useEffect(() => {
    setLeft(compute());
    if (deadline === null) return;
    const id = setInterval(() => setLeft(compute()), 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);
  return left;
}

/** Tracks a CSS media query. */
export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}
